<template>
  <validation-observer ref="form" tag="div">
    <validation-provider v-slot="{ validated, errors }" :name="lodash.startCase($t('purchase_payments.ticket_id'))"
                         rules="required"
                         vid="ticket_id">
      <b-form-group
          :invalid-feedback="errors[0]"
          :label="`${lodash.startCase($t('purchase_payments.ticket_id'))}*`"
          :state="errors[0] ? false : (validated ? true : null)"
          class="row mb-3"
          label-cols-md="2"
      >
        <delayed-multiselect-ajax
            :key="`${_uid}-tickets`"
            v-model.number="model.ticketable.ticket_id"
            :custom-label="({
           id,
           customer_type,
           created_at,
           creator: {
             name: creator_name
           } = {}
           }) => `${id}: ${$t(`tickets.customer_types.${customer_type}`)}, ${$t('created_by_{name}_on_{date}', {
             name: creator_name,
                date: $moment.utc(created_at).local().format('DD MMMM YYYY')
           })}`"
            :disabled="!!model.id"
            :modify-response="response => {
              const { data: { data } } = response;
              if (data.length) {
                const dataEdited = data.map(datum => {
                  return {
                    ...datum,
                  }
                })
                const dataEnabled = dataEdited.filter(datum => !datum.$isDisabled)
                response.data.data = [
                    ...dataEnabled,
                ]
              }
              return response;
            }"
            :params="{
              customer_type: 'lcs',
              available_to_add_trip: 1,
              pagination: 'simplePaginate',
              sort: 'id,desc',
              with: 'creator',
            }"
            name="ticket_id"
            url="/tickets"
            @update:selected="$set(model, 'ticket', $event); this.$set(this, 'model', {
          ...this.model,
          invoice_number: null,
          tripped_at: null,
          driver_id: null,
          vehicle_id: null,
          files: [{}],
          purchase_order_id: [],
          selected_purchase_orders: [],
          trip_items: []
        })"
        >
        </delayed-multiselect-ajax>
      </b-form-group>
    </validation-provider>
    <validation-provider v-slot="{ validated, errors }"
                         :name="lodash.startCase($t('purchase_payments.purchase_order_id'))" rules="required"
                         vid="selected_purchase_orders">
      <b-form-group
          :invalid-feedback="errors[0]"
          :label="lodash.startCase($t('purchase_payments.purchase_order_id')) + '*'"
          :state="errors[0] ? false : (validated ? true : null)"
          class="row mb-3"
          label-cols-md="2"
      >
        <multiselect-ajax
            :key="`purchase-payment-${model.ticketable.ticket_id}`"
            v-model="model.purchase_order_id"
            :custom-label="purchaseOrderLabel"
            :disabled="!model.ticketable.ticket_id"
            :modify-response="purchaseOrderModifyResponse"
            :multiple="true"
            :params="{
              with: 'customer,product,purchase_items.purchase_order.farmer,purchase_items.purchase_order.product',
              where_has: `tickets,id,${model.ticketable.ticket_id}`,
              type__not_in: 'down_payment,hybrid',
              stock_available: 1,
            }"
            :url="`/purchase_orders`"
            name="purchase_order_id"
            @update:selected="onPurchaseOrderSelected"
        >
        </multiselect-ajax>
      </b-form-group>
    </validation-provider>
    <b-form-group
        :label="`${lodash.upperFirst($t('products.singular'))}*`"
        class="row mb-3"
        label-cols-md="2"
    >
      <b-form-input
          v-model="model.selected_product_name"
          disabled
          name="shrimp_name"
          type="text"
      ></b-form-input>
    </b-form-group>
    <validation-provider v-slot="{ validated, errors }" :name="lodash.startCase($t('trips.invoice_number'))"
                         rules="required" vid="invoice_number">
      <b-form-group
          :invalid-feedback="errors[0]"
          :label="`${lodash.startCase($t('trips.invoice_number'))}*`"
          :state="errors[0] ? false : (validated ? true : null)"
          class="row mb-3"
          label-cols-md="2"
      >
        <b-form-input
            v-model="model.invoice_number"
            :state="errors[0] ? false : (validated ? true : null)"
            name="invoice_number"
            type="text"
        ></b-form-input>
      </b-form-group>
    </validation-provider>
    <validation-provider v-slot="{ validated, errors }" :name="lodash.startCase($t('trips.tripped_at'))"
                         :rules="trippedAtRules"
                         vid="tripped_at"
    >
      <b-form-group
          :invalid-feedback="errors[0]"
          :label="`${lodash.startCase($t('trips.tripped_at'))}*`"
          :state="errors[0] ? false : (validated ? true : null)"
          class="row mb-3"
          label-cols-md="2"
      >
        <date-picker
            v-model="model.tripped_at"
            :state="errors[0] ? false : (validated ? true : null)"
            format="DD MMMM YYYY"
            name="tripped_at"
            required
            type="date"
            @input="model.tripped_at = $event"
        >
        </date-picker>
      </b-form-group>
    </validation-provider>
    <validation-provider v-slot="{ validated, errors }" :name="lodash.startCase($t('trips.vehicle'))"
                         rules="required" vid="vehicle">
      <b-form-group
          :invalid-feedback="errors[0]"
          :label="`${lodash.startCase($t('trips.vehicle'))}*`"
          :state="errors[0] ? false : (validated ? true : null)"
          class="row mb-3"
          label-cols-md="2"
      >
        <multiselect-ajax
            :key="`${_uid}-vehicles`"
            v-model.number="model.vehicle_id"
            :custom-label="vehicleLabel"
            name="vehicle_id"
            url="/vehicles"
        >
          <template v-slot:beforeList>
            <b-button v-b-modal.vehicle-create-modal variant="link"
            >+ {{ $t('Add new {something}', {something: $t('trips.vehicle')}) }}
            </b-button>
            <vehicle-create-modal
                :id="`vehicle-create-modal`"
                :key="`vehicle-create-modal-${model.vehicle_id}`"
                @created="$set(model, 'vehicle_id', $event.id)"
            ></vehicle-create-modal>
          </template>
        </multiselect-ajax>
      </b-form-group>
    </validation-provider>
    <validation-provider v-slot="{ validated, errors }" :name="lodash.startCase($t('trips.driver'))"
                         rules="required" vid="driver">
      <b-form-group
          :invalid-feedback="errors[0]"
          :label="`${lodash.startCase($t('trips.driver'))}*`"
          :state="errors[0] ? false : (validated ? true : null)"
          class="row mb-3"
          label-cols-md="2"
      >
        <multiselect-ajax
            v-model.number="model.driver_id"
            :custom-label="(m) => `${m.name} - ${m.phone_number}`"
            name="driver_id"
            url="/drivers"
        >
          <template v-slot:beforeList>
            <b-button v-b-modal.driver-create-modal variant="link"
            >+ {{ $t('Add new {something}', {something: $t('trips.driver')}) }}
            </b-button>
            <driver-create-modal
                :id="`driver-create-modal`"
                :key="`driver-create-modal-${model.driver_id}`"
                @created="$set(model, 'driver_id', $event.id)"
            ></driver-create-modal>
          </template>
        </multiselect-ajax>
      </b-form-group>
    </validation-provider>
    <validation-provider
        v-slot="{ validated, errors }" :name="lodash.upperFirst($t('trips.to_processing'))"
        rules="required"
        vid="to_processing"
    >
      <b-form-group
          :invalid-feedback="errors[0]"
          :label="lodash.startCase($t('trips.to_processing')) + '*'"
          :state="errors[0] ? false : (validated ? true : null)"
          class="row mb-3"
          label-cols-md="2"
      >
        <multiselect-ajax
            :key="`${_uid}-customer_id`"
            v-model.number="model.customer_id"
            :custom-label="(m) => `${m.name} (${m.code})`"
            :params="{
              type: 'processing',
            }"
            name="customer_id"
            url="/customers"
            @update:selected="$set(model, 'customer', $event)"
        >
          <template v-slot:beforeList>
            <b-button v-b-modal.customer-create-modal variant="link"
            >+ {{ $t('Add new {something}', {something: $t('trips.to_processing')}) }}
            </b-button>
            <customer-create-modal
                :id="`customer-create-modal`"
                :key="`customer-create-modal-${model.customer_id}`"
                :deny-customer-select="true"
                :initial-value="{ type: ['processing'], registered: false }"
                @created="$set(model, 'customer_id', $event.id)"
            ></customer-create-modal>
          </template>
        </multiselect-ajax>
      </b-form-group>
    </validation-provider>
    <validation-provider
        v-slot="{ validated, errors }"
        name="attachment"
        rules="mimes:image/jpeg,image/png,application/pdf"
        vid="files.0.file"
    >
      <b-form-group
          :invalid-feedback="errors[0]"
          :label="lodash.upperFirst($t('purchase_orders.file'))"
          :state="errors[0] ? false : (validated ? true : null)"
          class="row mb-3"
          label-cols-md="2"
      >
          <b-form-file
              v-model="model.files[0].file"
              :drop-placeholder="lodash.upperFirst($t('drop file here'))"
              :name="`files.${0}`"
              :placeholder="Object.keys(model.files[0] ? model.files[0] : {}).length ? model.files[0].name : lodash.upperFirst($t('choose a file or drop it here'))"
              :state="errors[0] ? false : (validated ? true : null)"
              accept="image/jpeg,image/png,application/pdf"
              plain
              @change="onChangeFile($event, 0)"
          />
          <file-item
              class="mt-2"
              v-if="model.files[0].file && !errors.length"
              :file="model.files[0]"
          />
      </b-form-group>
    </validation-provider>
    <hr>
    <validation-provider v-slot="{ validated, errors }" :name="lodash.upperFirst($t('trips.creates.trip_item'))"
                         rules="required"
                         vid="has_trip_item">
      <b-form-group
          :invalid-feedback="errors[0]"
          :label="`${lodash.startCase($t('trips.creates.trip_item'))}*`"
          :state="errors[0] ? false : (validated ? true : null)"
          class="row mb-3"
          label-cols-md="2"
      >
        <b-button v-b-modal.modal-purchase-item :disabled="model.selected_purchase_orders.length === 0" class="mb-2"
                  variant="primary">
          {{ $t('Click to Choose {something}', {something: lodash.startCase($t(`trips.creates.trip_item`))}) }}
        </b-button>
        <b-input v-show="false" :value="model.has_trip_item ? 'filled':''"></b-input>
        <modal-select-item-purchase-order
            v-if="model.purchase_items.length"
            id="modal-purchase-item"
            :key="`modal-purchase-item-${model.selected_purchase_orders.map(v => v.id).join(',')}`"
            :fields="purchaseItemTableFields"
            :items="model.purchase_items"
            modal-title="Pilih Item Nota Panen"
            @update:items="handleChangePurchaseItems"
        />
        <template v-for="(tripItem, key) in model.trip_items">
          <div :key="`trip_items.key.${key}`" :class="`row g-2 ${key === 0 ? '' : 'mt-2'}`">
            <div class="col-1">
              <div :class="key===0?'mb-3':'mb-2'">{{ key === 0 ? 'No' : '&nbsp;' }}</div>
              <div>{{ key + 1 }}</div>
            </div>
            <div class="col-11">
              <div class="row">
                <b-form-group
                    :label="`${lodash.startCase($t('trip_items.purchase_order_id'))}*`"
                    class="col-md-2"
                >
                  <b-form-input
                      :value="`${tripItem.purchase_order_id}`"
                      disabled
                  />
                </b-form-group>
                <b-form-group
                    :label="`${lodash.startCase($t('products.singular'))}*`"
                    class="col-md-2"
                >
                  <b-form-input
                      :value="`${lodash.startCase(tripItem.purchase_order.product.name)}`"
                      disabled
                  />
                </b-form-group>
                <b-form-group
                    :label="`${lodash.startCase($t('trip_items.quality'))}*`"
                    class="col-md-2"
                >
                  <b-form-input
                      :value="`${$t('qualities.'+tripItem.quality)}`"
                      disabled
                  />
                </b-form-group>
                <validation-provider
                    v-slot="{ validated, errors }" :name="lodash.startCase($t('trip_items.quantity'))"
                    :rules="`required|gt:0|max_value:${tripItem.max_quantity}`"
                    :vid="`trip_items.${key}.quantity`"
                    class="col-md-2 mb-3">
                  <b-form-group
                      :invalid-feedback="errors[0]"
                      :label="`${lodash.startCase($t('trip_items.quantity'))}*`"
                      :state="errors[0] ? false : (validated ? true : null)"
                  >
                    <b-input-group :append="$t('units.kg')">
                      <numeral-input
                          id="quantity"
                          v-model="tripItem['quantity']"
                          :name="`trip_items[${key}][quantity]`"
                          :placeholder="lodash.startCase($t('trip_items.quantity'))"
                          :state="errors[0] ? false : (validated ? true : null)"
                          style="text-align: right"
                          @blur="handleChangeQuantity(key)"
                      />
                    </b-input-group>
                  </b-form-group>
                </validation-provider>
                <validation-provider v-slot="{ validated, errors }" :name="lodash.startCase($t('trip_items.size'))"
                                     :vid="`trip_items.${key}.size`" class="col-md-2 mb-3"
                                     rules="required|gt:0">
                  <b-form-group
                      :invalid-feedback="errors[0]"
                      :label="`${lodash.startCase($t('trip_items.size'))}*`"
                      :state="errors[0] ? false : (validated ? true : null)"
                  >
                    <b-input-group>
                      <numeral-input
                          id="size"
                          v-model="tripItem['size']"
                          :name="`trip_items[${key}][size]`"
                          :placeholder="lodash.startCase($t('trip_items.size'))"
                          :state="errors[0] ? false : (validated ? true : null)"
                          style="text-align: right"
                      />
                    </b-input-group>
                  </b-form-group>
                </validation-provider>
                <validation-provider v-slot="{ validated, errors }" :name="lodash.startCase($t('trip_items.label'))"
                                     rules="required"
                                     :vid="`trip_items.${key}.label`"
                                     class="col-md-2 mb-3">
                  <b-form-group
                      :invalid-feedback="errors[0]"
                      :label="lodash.startCase($t('trip_items.label')) + '*'"
                      :state="errors[0] ? false : (validated ? true : null)"
                  >
                    <b-form-input
                        id="label"
                        v-model="tripItem['label']"
                        :formatter="value => value.trimStart().trimEnd().toUpperCase()"
                        :name="`trip_items[${key}][label]`"
                        :placeholder="lodash.startCase($t('trip_items.label'))"
                        :state="errors[0] ? false : (validated ? true : null)"
                        lazy-formatter
                    ></b-form-input>
                  </b-form-group>
                </validation-provider>
                <validation-provider
                    v-slot="{validated,errors}" :name="lodash.startCase($t('trip_items.unit_price'))"
                    :rules="itemUnitPriceRules"
                    :vid="`trip_items.${key}.unit_price`"
                    class="col-md-3 mb-3"
                >
                  <b-form-group
                      :invalid-feedback="errors[0]"
                      :label="`${lodash.startCase($t('trip_items.unit_price'))}*`"
                      :state="errors[0] ? false : (validated ? true : null)"
                  >
                    <numeral-input
                        id="unit_price"
                        v-model="tripItem['unit_price']"
                        disabled="true"
                        :name="`trip_items[${key}][unit_price]`"
                        :placeholder="lodash.startCase($t('trip_items.unit_price'))"
                        :state="errors[0] ? false : (validated ? true : null)"
                        format="$ 0,0.[00]"
                        lazy-formatter
                        required
                        style="text-align: right"
                        @blur="updateTotalPriceItem(key)"
                    />
                  </b-form-group>
                </validation-provider>
                <validation-provider
                    v-slot="{ validated, errors }" :name="lodash.startCase($t('trip_items.total_price'))"
                    :rules="itemUnitPriceRules"
                    class="col-md-3 mb-3"
                >
                  <b-form-group
                      :invalid-feedback="errors[0]"
                      :label="`${lodash.startCase($t('trip_items.total_price'))}*`"
                      :state="errors[0] ? false : (validated ? true : null)"
                  >
                    <numeral-input
                        id="total_unit_price"
                        v-model="tripItem.total_price"
                        :disabled="true"
                        :name="`trip_items[${key}][total_price]`"
                        :placeholder="lodash.startCase($t('trip_items.total_price'))"
                        :state="errors[0] ? false : (validated ? true : null)"
                        format="$ 0,0.[00]"
                        style="text-align: right"
                    />
                  </b-form-group>
                </validation-provider>
                <validation-provider
                    v-slot="{ validated, errors }"
                    :name="lodash.startCase($t('trip_items.farmer'))"
                    :vid="`trip_items.${key}.farmer`"
                    class="col-md-3 mb-3"
                >
                  <b-form-group
                      :invalid-feedback="errors[0]"
                      :label="`${lodash.startCase($t('trip_items.farmer'))}*`"
                      :state="errors[0] ? false : (validated ? true : null)"
                  >
                    <b-form-input
                        :key="`${_uid}-farmers`"
                        :state="errors[0] ? false : (validated ? true : null)"
                        :value="farmerLabel(tripItem)"
                        disabled
                        name="farmer"
                    />
                  </b-form-group>
                </validation-provider>
              </div>
            </div>
          </div>
        </template>
      </b-form-group>
    </validation-provider>
    <div class="row">
      <b-form-group
          :label="`${lodash.startCase($t('trips.total_item_overall'))}:`"
          class="mb-3 col-md-6 pe-md-0"
          content-cols="12"
          content-cols-md="8"
          label-cols="12"
          label-cols-md="4"
      >
        <b-form-input
            :name="`totalAllItem`"
            :value="model.trip_items.length"
            disabled
            style="text-align: right"
        ></b-form-input>
      </b-form-group>
      <b-form-group
          :label="`${lodash.startCase($t('trips.total_quantity_overall'))}:`"
          class="col-md-6 mb-3"
          content-cols="12"
          content-cols-md="8"
          label-cols="12"
          label-cols-md="4"
      >
        <b-input-group :append="$t('units.kg')">
          <b-form-input
              :name="`totalAllBiomassa`"
              :value="$numeral(tripItemTotalQuantity).format('0,0.[000]')"
              disabled
              style="text-align: right"
          ></b-form-input>
        </b-input-group>
      </b-form-group>
    </div>
    <b-form-group
        class="row mb-3"
        label="Sisa Biomassa Nota Panen - Biomassa Nota Armada"
        label-cols-md="2"
    >
      <b-input-group :append="$t('units.kg')">
        <b-form-input
            :value="$numeral(purchaseOrderTotalAvailableQuantity - tripItemTotalQuantity).format('0,0.[000]')"
            disabled
            name="difference"
            style="text-align: right"
        ></b-form-input>
      </b-input-group>
    </b-form-group>
    <hr>
    <validation-provider v-slot="{ validated, errors }" :name="lodash.startCase($t('models.description'))" rules=""
                         vid="description">
      <b-form-group
          :invalid-feedback="errors[0]"
          :label="lodash.startCase('catatan')"
          :state="errors[0] ? false : (validated ? true : null)"
          class="row mb-3"
      >
        <editor
            v-if="$parent.$parent.$options.name !== 'BModal'"
            ref="editor"
            v-model="model.description"
            :init="editorInit"
            class="form-control"
            inline
            plugins="lists link image paste help wordcount"
            toolbar="undo redo | styleselect | fontselect | bold italic | alignleft aligncenter alignright alignjustify | outdent indent"
        />
        <b-form-textarea v-else v-model="model.description"/>
      </b-form-group>
    </validation-provider>
  </validation-observer>
</template>

<script>
import DelayedMultiselectAjax from "@/components/inputs/vue-multiselect-ajax";
import FormModel from './model'
import ModalSelectItemPurchaseOrder from '@/components/modals/modal-select-item-purchase-order'
import VehicleCreateModal from '@/components/modals/vehicle-create'
import DriverCreateModal from '@/components/modals/driver-create'
import CustomerCreateModal from '@/components/modals/customer-create'
import _ from "lodash";
import FileItem from "@/components/inputs/file-item.vue";

export default {
  extends: FormModel,
  components: {
    FileItem,
    DelayedMultiselectAjax,
    DriverCreateModal,
    VehicleCreateModal,
    CustomerCreateModal,
    ModalSelectItemPurchaseOrder
  },
  data() {
    return {

      selectedPurchaseItems: [],
      quality_options: [
        {value: 'fresh', text: 'Fresh'},
        {value: 'km', text: 'Kulit Muda (KM)'},
        {value: 'bs', text: 'Below Standard (BS)'}
      ],
      funding_options: [
        {value: 'fresh', text: 'Fresh'},
        {value: 'kmtb', text: 'Titip Bongkar'}
      ],
      firstFile: null,
      origin_trip_items: [
        ...((this.value.trip_items || []).length ? this.value.trip_items : []),
      ],
    }
  },
  computed: {
    latestPurchaseOrderDate() {
      let dateString = this.model.selected_purchase_orders[0]?.ordered_at
      let latest = dateString ? new Date(dateString) : new Date()
      this.model.selected_purchase_orders.forEach(({ordered_at}) => {
        let date = new Date(ordered_at)
        if (date > latest) latest = date
      })
      return latest
    },
    trippedAtRules() {
      return {
        required: true,
        after_or_equal: this.$moment(this.minimumDate.ordered_at).format('YYYY-MM-DD'),
        before_or_equal: this.$moment().format("YYYY-MM-DD"),
      }
    },
    isDepositGoodOrder() {
      let purchaseOrder = this.model.selected_purchase_orders[0]
      return purchaseOrder?.type === 'deposit_goods'
    },
    itemUnitPriceRules() {
      if (this.isDepositGoodOrder) return 'required'
      return 'required|gt:0'
    },
    isCreateTripAgain() {
      return !!localStorage.getItem('createTripAgain')
    },
    modelDefaultValue() {
      let defaultValue = {
        type: null,
        purchase_items: [],
        purchase_order_id: [],
        files: [
          ...((this.value.files || []).length ? this.value.files : [{}]),
        ],
        trip_items: [
          ...((this.value.trip_items || []).length ? this.value.trip_items : []),
        ],
        selected_purchase_orders: [
          ...((this.value.selected_purchase_orders || []).length ? this.value.selected_purchase_orders : []),
        ],
        ticket: !this.lodash.isEmpty(this.value.ticket) ? this.value.ticket : {},
        ticketable: !this.lodash.isEmpty(this.value.ticketable) ? this.value.ticketable : {},
        difference: 0,
      }
      let newModelValue = localStorage.getItem('createTripAgain')

      if (newModelValue) {
        newModelValue = JSON.parse(newModelValue)
        defaultValue = {
          ...defaultValue,
          ...newModelValue,
        }
      }
      return this.lodash.cloneDeep(defaultValue)
    },
    purchaseOrderTotalAvailableQuantity() {
      return this.model.selected_purchase_orders.reduce((total, purchaseOrder) => {
        let totalAvailableQuantity = purchaseOrder.purchase_item_total_available_quantity || 0
        return total + (totalAvailableQuantity > 0 ? totalAvailableQuantity : 0)
      }, 0)
    },
    selectedProductName() {
      if (!this.model.selected_purchase_orders.length) return '-'
      return [...new Set(this.model.selected_purchase_orders.map(({product}) => product.name).sort((a, b) => a.localeCompare(b)))].join(',')
    },
    defaultValueModalGenerate() {
      if (this.model.trip_items.length) return this.model.trip_items.map(i => i.purchase_item_id)
      else return []
    },
    purchaseItemTableFields() {
      return [
        {
          key: 'index',
          label: `${this.lodash.startCase(this.$t('trips.choose'))}`
        },
        {
          key: 'fund',
          label: `${this.lodash.startCase(this.$t('purchase_items.fund'))}`,
          formatter: (value) => this.lodash.startCase(value)
        },
        {
          key: 'quantity',
          label: `${this.lodash.startCase(this.$t('purchase_items.quantity'))}`,
          tdClass: 'text-end',
          formatter: (value) => this.$numeral(value).format('0,0.[000]')
        },
        {
          key: 'available_quantity',
          label: `${this.lodash.startCase(this.$t('purchase_items.available_quantity'))}`,
          tdClass: 'text-end',
          formatter: (value) => this.$numeral(value).format('0,0.[000]')
        },
        {
          key: 'size',
          label: `${this.lodash.startCase(this.$t('purchase_items.size'))}`,
          tdClass: 'text-end',
          formatter: (value) => this.$numeral(value).format('0,0.[000]')
        },
        {
          key: 'unit_price',
          label: `${this.lodash.startCase(this.$t('purchase_items.unit_price'))}`,
          tdClass: 'text-end',
          formatter: (value) => this.$numeral(value).format('0,0.[000]')
        },
        {
          key: 'total_price',
          label: `${this.lodash.startCase(this.$t('purchase_items.total_price'))}`,
          tdClass: 'text-end',
          formatter: (value) => this.$numeral(value).format('0,0.[000]')
        },
      ]
    },
    tripItemTotalQuantity() {
      return Math.round(this.model.trip_items.reduce((total, tripItem) => total + tripItem.quantity, 0) * 1000) / 1000
    },
    minimumDate() {
      const purchaseOrder = this.model.selected_purchase_orders;
      const latestPurchaseOrder = purchaseOrder.sort((a, b) => new Date(b.ordered_at) - new Date(a.ordered_at))?.[0] || {};
      return latestPurchaseOrder
    }
  },
  watch: {
    'model.purchase_items': {
      deep: true,
      handler: function (val, oldVal) {
        if (!this.lodash.isEqual(val, oldVal)) {
          this.selectedPurchaseItems = this.lodash.cloneDeep(val.filter(purchaseItem => purchaseItem.$checkbox && purchaseItem.$selected))
        }
      }
    },
    'selectedPurchaseItems': function (val, oldVal) {
      if (!this.lodash.isEqual(val, oldVal)) {
        let tripItems = this.lodash.cloneDeep(val.map(v => {
          return {
            purchase_order: v.purchase_order,
            purchase_order_id: v.purchase_order_id,
            purchase_item_id: v.id,
            fund: v.fund,
            quality: v.quality,
            size: v.size,
            quantity: v.available_quantity,
            max_quantity: v.available_quantity,
            unit_price: v.unit_price,
            total_price: v.available_quantity * v.unit_price,
            customer_id: v.purchase_order.farmer_id,
          }
        }))
        this.$set(this.model, 'trip_items', tripItems)
        this.model.difference = this.purchaseOrderTotalAvailableQuantity - this.tripItemTotalQuantity
      }
    },
    'model.trip_items': {
      handler: function (val, oldVal) {
        if (!this.lodash.isEqual(val, oldVal)) {
          this.model.has_trip_item = !!val.length
        }
      },
      deep: true,
    },
    'model.selected_purchase_orders': function (val, oldVal) {
      if (!this.lodash.isEqual(val, oldVal)) {
        this.model.purchase_items = this.getPurchaseItemTableItems(val)
        this.selectedPurchaseItems = []

        this.$set(this.model, 'selected_product_name', this.selectedProductName)
      }
    },
  },
  created() {
    this.selectedPurchaseItems = []
    this.$set(this, 'model', this.modelDefaultValue)
  },
  methods: {
    farmerLabel(tripItem) {
      return tripItem.purchase_order.farmer?.name ?? '-'
    },
    purchaseOrderLabel(purchaseOrder) {
      return `${purchaseOrder.id} (${purchaseOrder.customer.name} - ${purchaseOrder.sub_product_name} - ${this.$moment.utc(purchaseOrder.ordered_at).local().format('DD MMM YYYY')})`
    },
    setModelType: _.debounce(function (type) {
      this.$set(this.model, 'type', type)
    }, 500),
    purchaseOrderModifyResponse(payload) {
      const {data: {data}} = payload;
      if (data.length) {
        let orderType = this.model?.selected_purchase_orders[0]?.type
        let serviceFee = this.model?.selected_purchase_orders[0]?.service_fee
        const dataEdited = data.map(datum => {
          datum.is_same_product = orderType ? datum.type === orderType : true
          datum.is_same_service_fee = serviceFee ? datum.service_fee === serviceFee : true

          return {
            ...datum,
            $isDisabled:  (orderType !== null) && (!datum.is_same_product || !datum.is_same_service_fee),
          }
        })
        const dataEnabled = dataEdited.filter(datum => !datum.$isDisabled)
        const dataDisabled = dataEdited.filter(datum => datum.$isDisabled)
        payload.data.data = [
          ...dataEnabled,
          ...dataDisabled,
        ]
      }
      return payload;
    },
    vehicleLabel(vehicle) {
      let vehicleName = vehicle?.name

      if (vehicle?.type === 'heavy') vehicleName = this.$t('vehicles.truck')
      else if (vehicle?.type === 'medium') vehicleName = this.$t('vehicles.crank_truck')
      else if (vehicle?.type === 'light') vehicleName = this.$t('vehicles.pickup')

      return vehicle ? `${this.lodash.upperFirst(vehicleName)} - ${vehicle.license_plate}` : ''
    },
    quantityMaxValueCalculation(purchaseItemId) {
      let max_limit = this.purchaseItemOptions.find(i => i.purchase_item_id === purchaseItemId)
      if (max_limit && max_limit.quantity) return max_limit.quantity
      else return 100000000000000
    },
    onPurchaseOrderSelected(payload) {
      let purchaseOrderType = null
      if (payload) {
        this.$set(this.model, 'selected_purchase_orders', payload)
        purchaseOrderType = payload?.[0]?.type
        if (!payload.length) this.$set(this.model, 'trip_items', [])
      } else {
        this.$set(this.model, 'selected_purchase_orders', [])
      }

      this.setModelType(purchaseOrderType)
    },
    onPurchaseItemSelected(payload) {
      let data = this.lodash.flatten(this.lodash.map(this.model.selected_purchase_orders, item => item.purchase_items))
      if (data.length) {
        data = data.filter(i => i)
        data = data.map(function (dta) {
          let newQuantity = (dta.quantity - (dta.trip_item_total_quantity + dta.sale_item_total_quantity))
          let newData = {
            ...dta,
            purchase_item_id: dta.id,
            quantity: newQuantity,
            total_price: newQuantity * dta.unit_price
          }
          delete newData.id
          return newData
        })
        data = data.filter(function (item) {
          return payload.find(i => (i === item.purchase_item_id))
        })
        let tripItems = [...this.model.trip_items]
        if (tripItems.length) {
          data = data.map(function (dta) {
            let tripItem = tripItems.find(i => i.purchase_item_id === dta.purchase_item_id)
            if (tripItem) return tripItem
            else return dta
          })
        }
      }
      this.model.trip_items = data
      this.model.difference = this.differenceBiomass()
    },
    onChangeFile(event, index) {
      let vm = this
      let reader = new FileReader()
      let data = event.target.files[0]
      if (!data) {
        setTimeout(function () {
          vm.model.files[0].file = vm.firstFile[0].file
          return null
        }, 1000);
      }
      reader.readAsDataURL(data)
      reader.onload = () => {
        let file = event.target.files[index]
        file.format = file.type;
        file.extension = file.name.split(".").pop();
        file.sizeInBytes = file.size;
        file.sizeInKB = (file.size / 1024).toFixed(2);
        file.sizeInMB = (file.size / (1024 * 1024)).toFixed(2);
        this.$set(this.model, 'files', [{
          file,
          url: URL.createObjectURL(file),
          name: file.name,
          format: file.format,
          extension: file.extension,
          sizeInBytes: file.sizeInBytes,
          sizeInKB: file.sizeInKB,
          sizeInMB: file.sizeInMB
        }]);
        this.$set(this, 'firstFile', [{...file}]);
        // this.updateLocalStorage();
      };
    },
    differenceBiomass() {
      if (this.purchaseItemOptions.length && this.purchaseItemOptions[0])
        return this.purchaseItemOptions.reduce((total, v) => total + (v.quantity || 0), 0) - this.countBiomass()
      else return null
    },
    updateTotalPriceItem(index) {
      this.model.trip_items[index].total_price = this.model.trip_items[index].quantity * this.model.trip_items[index].unit_price
    },
    dataURLtoFile(dataUrl, filename) {
      let arr = dataUrl.split(','),
          mime = arr[0].match(/:(.*?);/)[1],
          bstr = atob(arr[1]),
          n = bstr.length,
          u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new File([u8arr], filename, {type: mime});
    },
    getPurchaseItemTableItems(purchaseOrders) {
      let items = []
      purchaseOrders.forEach(purchaseOrder => {
        items.push({
          $checkbox: true,
          $purchaseOrder: true,
          purchase_order_id: purchaseOrder.id,
          product: purchaseOrder.product,
        })
        purchaseOrder.purchase_items.forEach((purchaseItem, index) => {
          if (this.model.id) {
            items.push({
              ...purchaseItem,
              customer_id: purchaseOrder.customer_id,
              $checkbox: true,
              $purchaseOrder: false,
              product: purchaseOrder.product,
              disabled: purchaseItem.available_quantity <= 0,
              _rowVariant: purchaseItem.available_quantity <= 0 ? 'secondary' : '',
              _index: index,
            })
          } else {
            items.push({
              ...purchaseItem,
              customer_id: purchaseOrder.customer_id,
              $checkbox: true,
              $purchaseOrder: false,
              product: purchaseOrder.product,
              disabled: purchaseItem.available_quantity <= 0,
              _rowVariant: purchaseItem.available_quantity <= 0 ? 'secondary' : '',
              _index: index,
            })
          }
        })
      })

      return items
    },
    handleChangeQuantity(tripItemIndex) {
      this.updateTotalPriceItem(tripItemIndex)
      this.model.difference = this.purchaseOrderTotalAvailableQuantity - this.tripItemTotalQuantity
    },
    handleChangePurchaseItems(purchaseItems) {
      this.model.purchase_items = purchaseItems
    }
  },

}
</script>
